import { useCallback, useState } from "react";
import api from "@/lib/api";

// Discovery module (AP-02). All calls go through the Studio auth proxy
// /api/os/discovery/* (AP-01) — the UI is a pure client of module state
// living in Supabase (D-6: API-first, UI is only a client).

const LONG_CALL = { timeout: 150000 }; // WF3 structuring measured at 45-60s
const CALL = { timeout: 90000 };

export const PHASES = ["kickoff", "interview", "readiness", "summary"];

function inferredIds(state) {
  return new Set((state?.inferred || []).map((i) => i.question_id));
}

function artifactsUsable(state) {
  const arts = state?.artifacts || [];
  const yaml = arts.find((a) => a.artifact_type === "project_yaml");
  return Boolean(yaml && ((yaml.content_yaml || "").trim() || (yaml.content_markdown || "").trim()));
}

export function phaseForState(state) {
  if (artifactsUsable(state)) return "summary";
  if (state?.context?.done) return "readiness";
  return "interview";
}

export default function useDiscovery() {
  const [phase, setPhase] = useState("kickoff");
  const [sessionId, setSessionId] = useState(null);
  const [state, setState] = useState(null);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [freshInferred, setFreshInferred] = useState([]);
  const [busy, setBusy] = useState(null); // null | intake | answer | readiness | artifacts | resume
  const [error, setError] = useState(null);

  const fetchState = useCallback(async (sid) => {
    const r = await api.post("/os/discovery/state", { session_id: sid }, CALL);
    setState(r.data);
    return r.data;
  }, []);

  const start = useCallback(
    async ({ problem, projectName, clientName, clientEmail }) => {
      setBusy("intake");
      setError(null);
      try {
        const r = await api.post(
          "/os/discovery/intake",
          {
            problem,
            project_name: projectName,
            client_name: clientName || "Studio-Demo",
            client_email: clientEmail || "demo@appwerkstatt.local",
            created_by: "studio",
          },
          CALL
        );
        const sid = r.data.session_id;
        setSessionId(sid);
        setCurrentQuestionId(r.data.next_question?.next_question_id || null);
        await fetchState(sid);
        setFreshInferred([]);
        setPhase("interview");
      } catch (e) {
        setError(msgFor(e, "Die Discovery konnte nicht gestartet werden."));
      } finally {
        setBusy(null);
      }
    },
    [fetchState]
  );

  const runReadiness = useCallback(
    async (sid) => {
      await api.post("/os/discovery/readiness", { session_id: sid }, CALL);
      await fetchState(sid);
      setPhase("readiness");
    },
    [fetchState]
  );

  const answer = useCallback(
    async (text) => {
      if (!sessionId || !currentQuestionId) return;
      setBusy("answer");
      setError(null);
      const before = inferredIds(state);
      try {
        const r = await api.post(
          "/os/discovery/next",
          {
            session_id: sessionId,
            question_id: currentQuestionId,
            answer: text,
            answered_by: { name: "Studio", role: "process_owner" },
          },
          CALL
        );
        const nq = r.data.next_question || {};
        const fresh = await fetchState(sessionId);
        setFreshInferred([...inferredIds(fresh)].filter((id) => !before.has(id)));
        if (nq.done) {
          await runReadiness(sessionId);
        } else {
          setCurrentQuestionId(nq.next_question_id || null);
        }
      } catch (e) {
        setError(msgFor(e, "Die Antwort konnte nicht verarbeitet werden."));
      } finally {
        setBusy(null);
      }
    },
    [sessionId, currentQuestionId, state, fetchState, runReadiness]
  );

  // WF3 is measured as stochastically fragile (AP-03): it can report ok while
  // artifacts stay empty. We verify content and let the user retry.
  const generateArtifacts = useCallback(async () => {
    if (!sessionId) return;
    setBusy("artifacts");
    setError(null);
    try {
      await api.post("/os/discovery/structure", { session_id: sessionId }, LONG_CALL);
      await api.post("/os/discovery/summary", { session_id: sessionId }, LONG_CALL);
      const fresh = await fetchState(sessionId);
      if (!artifactsUsable(fresh)) {
        setError("Die Artefakte kamen leer zurück (bekanntes Verhalten der Strukturierung). Bitte noch einmal erzeugen — der zweite Lauf ist in der Regel sauber.");
        return;
      }
      setPhase("summary");
    } catch (e) {
      setError(msgFor(e, "Die Artefakte konnten nicht erzeugt werden."));
    } finally {
      setBusy(null);
    }
  }, [sessionId, fetchState]);

  const resume = useCallback(
    async (sid) => {
      setBusy("resume");
      setError(null);
      try {
        const fresh = await fetchState(sid.trim());
        setSessionId(sid.trim());
        setFreshInferred([]);
        const p = phaseForState(fresh);
        if (p === "interview") {
          setCurrentQuestionId(fresh.context?.candidates?.[0]?.id || null);
        }
        setPhase(p);
      } catch (e) {
        setError(msgFor(e, "Diese Session konnte nicht geladen werden. Stimmt die Session-ID?"));
      } finally {
        setBusy(null);
      }
    },
    [fetchState]
  );

  const reset = useCallback(() => {
    setPhase("kickoff");
    setSessionId(null);
    setState(null);
    setCurrentQuestionId(null);
    setFreshInferred([]);
    setBusy(null);
    setError(null);
  }, []);

  return {
    phase,
    sessionId,
    state,
    currentQuestionId,
    freshInferred,
    busy,
    error,
    start,
    answer,
    generateArtifacts,
    resume,
    reset,
    setError,
  };
}

function msgFor(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (e?.code === "ECONNABORTED") return "Der Workflow braucht länger als erwartet. Bitte erneut versuchen.";
  return fallback;
}
